import { useRef, useEffect, useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  togglePlay,
  seekToSeconds,
  updateCurrentTime,
  setTotalSeconds,
  setPlaying,
  clearAutoplay,
  clearSeek,
} from '@/features/player/playerSlice';
import { formatTimestamp, secondsToPercent } from '@/utils/formatters';
import styles from './VideoPlayer.module.css';

const DEFAULT_VIDEO_SRC = '/assets/real-value-of-ai.mp4';

const DEFAULT_SCRUB_MARKS = [
  { position: 0.1,  label: 'Pre-production' },
  { position: 29.1, label: 'AI created video', active: true },
  { position: 83.5, label: 'Not there yet' },
];

export default function VideoPlayer() {
  const dispatch = useAppDispatch();
  const { isPlaying, currentSeconds, totalSeconds, pendingAutoplay, pendingSeekSeconds } = useAppSelector((s) => s.player);
  const activeAsset = useAppSelector((s) => s.search.activeAsset);
  const pendingAutoplayRef = useRef(pendingAutoplay);
  useEffect(() => { pendingAutoplayRef.current = pendingAutoplay; }, [pendingAutoplay]);
  const pendingSeekRef = useRef(pendingSeekSeconds);
  useEffect(() => { pendingSeekRef.current = pendingSeekSeconds; }, [pendingSeekSeconds]);
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<HTMLDivElement>(null);

  const videoSrc = activeAsset?.storage_url || DEFAULT_VIDEO_SRC;
  const scrubMarks = activeAsset ? [] : DEFAULT_SCRUB_MARKS;

  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [controlsHidden, setControlsHidden] = useState(false);
  const controlsHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Center play icon — visible when paused or for 1 s after play starts
  const [showCenterPlay, setShowCenterPlay] = useState(true);
  const centerPlayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mute state
  const [isMuted, setIsMuted] = useState(false);

  // Buffering state
  const [isBuffering, setIsBuffering] = useState(false);

  const handleWaiting = useCallback(() => setIsBuffering(true), []);
  const handleCanPlay = useCallback(() => setIsBuffering(false), []);

  // Reset playback position when video source changes
  useEffect(() => {
    dispatch(setPlaying(false));
    dispatch(updateCurrentTime(0));
  }, [videoSrc, dispatch]);

  // Sync play / pause + center-play-icon auto-hide
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) {
      video.play().catch(() => dispatch(setPlaying(false)));
      // Show center icon briefly then hide after 1 s
      setShowCenterPlay(true);
      centerPlayTimerRef.current && clearTimeout(centerPlayTimerRef.current);
      centerPlayTimerRef.current = setTimeout(() => setShowCenterPlay(false), 1000);
    } else {
      video.pause();
      // Always show when paused
      centerPlayTimerRef.current && clearTimeout(centerPlayTimerRef.current);
      setShowCenterPlay(true);
      // If paused in fullscreen, keep controls visible
      if (isFullscreen) {
        setControlsHidden(false);
        controlsHideTimerRef.current && clearTimeout(controlsHideTimerRef.current);
      }
    }
  }, [isPlaying, dispatch, isFullscreen]);

  // Seek when Redux currentSeconds jumps > 0.5s away from video time
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (Math.abs(video.currentTime - currentSeconds) > 0.5) {
      video.currentTime = currentSeconds;
    }
  }, [currentSeconds]);

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (video) dispatch(updateCurrentTime(video.currentTime));
  }, [dispatch]);

  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    dispatch(setTotalSeconds(Math.floor(video.duration)));
    if (pendingSeekRef.current !== null) {
      const seekSec = pendingSeekRef.current;
      video.currentTime = seekSec;
      dispatch(updateCurrentTime(seekSec));
      dispatch(setPlaying(true));
      dispatch(clearSeek());
    } else if (pendingAutoplayRef.current) {
      dispatch(setPlaying(true));
      dispatch(clearAutoplay());
    }
  }, [dispatch]);

  const handleEnded = useCallback(() => {
    dispatch(setPlaying(false));
  }, [dispatch]);

  const progress = secondsToPercent(currentSeconds, totalSeconds);
  const currentLabel = formatTimestamp(Math.floor(currentSeconds));
  const totalLabel = formatTimestamp(totalSeconds);

  const handleScrubClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    dispatch(seekToSeconds(pct * totalSeconds));
  };

  const [hoverPct, setHoverPct] = useState<number | null>(null);

  const handleScrubMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setHoverPct(Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)));
  };

  const handleScrubMouseLeave = () => setHoverPct(null);

  const hoverSeconds = hoverPct != null ? Math.floor(hoverPct * totalSeconds) : null;

  // ─── Fullscreen ──────────────────────────────────────────────────────────

  const toggleFullscreen = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;
    if (!document.fullscreenElement) {
      player.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen();
    }
  }, []);

  useEffect(() => {
    const onFsChange = () => {
      const entering = !!document.fullscreenElement;
      setIsFullscreen(entering);
      if (!entering) {
        setControlsHidden(false);
        controlsHideTimerRef.current && clearTimeout(controlsHideTimerRef.current);
      }
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // Reset the 2s inactivity timer for fullscreen controls
  const resetControlsHideTimer = useCallback(() => {
    setControlsHidden(false);
    controlsHideTimerRef.current && clearTimeout(controlsHideTimerRef.current);
    controlsHideTimerRef.current = setTimeout(() => setControlsHidden(true), 2000);
  }, []);

  // Start hide timer as soon as fullscreen opens
  useEffect(() => {
    if (isFullscreen && isPlaying) {
      resetControlsHideTimer();
    }
    return () => { controlsHideTimerRef.current && clearTimeout(controlsHideTimerRef.current); };
  }, [isFullscreen, isPlaying, resetControlsHideTimer]);

  const handlePlayerMouseMove = useCallback(() => {
    if (isFullscreen) resetControlsHideTimer();
  }, [isFullscreen, resetControlsHideTimer]);

  // ─── Mute ────────────────────────────────────────────────────────────────

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  }, []);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      centerPlayTimerRef.current && clearTimeout(centerPlayTimerRef.current);
      controlsHideTimerRef.current && clearTimeout(controlsHideTimerRef.current);
    };
  }, []);

  // ─── Render ──────────────────────────────────────────────────────────────

  const playerClass = [
    styles.player,
    controlsHidden ? styles.controlsHidden : '',
    isPlaying ? styles.isPlaying : '',
  ].filter(Boolean).join(' ');

  return (
    <div ref={playerRef} className={playerClass} onMouseMove={handlePlayerMouseMove}>
      {/* Real video element — key forces full remount when src changes */}
      <video
        key={videoSrc}
        ref={videoRef}
        src={videoSrc}
        className={styles.videoEl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onWaiting={handleWaiting}
        onCanPlay={handleCanPlay}
        onPlaying={handleCanPlay}
        preload="auto"
        playsInline
      />

      {/* Buffering spinner — shows in-place without stopping playback state */}
      {isBuffering && (
        <div className={styles.bufferingOverlay} aria-label="Buffering">
          <div className={styles.bufferingSpinner} />
        </div>
      )}

      {/* Center play/pause overlay — visible when paused, hides 1 s after play starts */}
      <button
        className={`${styles.playBtn}${showCenterPlay ? ` ${styles.playBtnVisible}` : ''}`}
        onClick={() => dispatch(togglePlay())}
        aria-label={isPlaying ? 'Pause' : 'Play'}
      >
        <div className={styles.playBtnInner}>
          {isPlaying ? (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="white" aria-hidden="true">
              <rect x="6" y="5" width="4" height="14" />
              <rect x="14" y="5" width="4" height="14" />
            </svg>
          ) : (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="white" aria-hidden="true">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </div>
      </button>

      {/* Timestamp */}
      <div className={styles.timestamp} aria-label="Current time">
        {currentLabel} / {totalLabel}
      </div>

      {/* Scrub bar & controls */}
      <div className={styles.scrub}>
        <div
          className={styles.scrubTrack}
          onClick={handleScrubClick}
          onMouseMove={handleScrubMouseMove}
          onMouseLeave={handleScrubMouseLeave}
          role="slider"
          aria-label="Video scrubber"
          aria-valuemin={0}
          aria-valuemax={totalSeconds}
          aria-valuenow={Math.floor(currentSeconds)}
        >
          <div className={styles.scrubProg} style={{ width: `${progress}%` }} />
          {scrubMarks.map((mark) => (
            <span
              key={mark.label}
              className={`${styles.scrubMark} ${mark.active ? styles.scrubMarkActive : ''}`}
              style={{ left: `${mark.position}%` }}
              title={mark.label}
            />
          ))}
          {hoverPct != null && hoverSeconds != null && (
            <div
              className={styles.scrubTooltip}
              style={{ left: `${hoverPct * 100}%` }}
            >
              {formatTimestamp(hoverSeconds)}
            </div>
          )}
        </div>

        <div className={styles.controls}>
          {/* Rewind 5 s */}
          <button
            className={styles.controlBtn}
            aria-label="Rewind 5s"
            onClick={() => dispatch(seekToSeconds(Math.max(0, currentSeconds - 5)))}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
              <text x="8.5" y="15.5" fontSize="5.5" fontWeight="bold" textAnchor="middle" fill="currentColor">5</text>
            </svg>
          </button>

          {/* Play / Pause */}
          <button
            className={styles.controlBtn}
            onClick={() => dispatch(togglePlay())}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <rect x="6" y="5" width="4" height="14" />
                <rect x="14" y="5" width="4" height="14" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>

          {/* Forward 5 s */}
          <button
            className={styles.controlBtn}
            aria-label="Forward 5s"
            onClick={() => dispatch(seekToSeconds(Math.min(totalSeconds, currentSeconds + 5)))}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 5V1l5 5-5 5V7c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8z"/>
              <text x="15.5" y="15.5" fontSize="5.5" fontWeight="bold" textAnchor="middle" fill="currentColor">5</text>
            </svg>
          </button>

          {/* Mute / Unmute */}
          <button
            className={styles.controlBtn}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
            onClick={toggleMute}
          >
            {isMuted ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27l4.73 4.73H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
              </svg>
            )}
          </button>

          <div className={styles.controlsSpacer}>
            {/* Fullscreen toggle */}
            <button
              className={styles.controlBtn}
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              onClick={toggleFullscreen}
            >
              {isFullscreen ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
